/**
 * AgedCarePage – container for the aged care module: the landing on a saved plan, the analyser, and
 * the guide as a reference that never loses the reader's place (UX review, 17 Oct 2026).
 *
 * WHERE IT OPENS.
 *   - A plan is being reopened (Home's "Open", or "Model another home"): straight into the analyser,
 *     which lands on the plan rather than on an empty step 1.
 *   - "Start a new model from scratch": the analyser on an empty step 1, asking the setup questions again.
 *   - Plans exist on the file: the landing — this home's chosen plan, one column.
 *   - Nothing modelled yet: step 1 of the analyser, with the fees primer open and the guide a link.
 *
 * THE GUIDE IS A LINK, NOT A GATE (replacing the 22 Sep 2026 guide-first ruling, by the owner's
 * decision of 17 Oct). It used to stand in front of every route in and to unmount the analyser when
 * opened from inside it, losing the room price and the step. Now it opens over the page the reader
 * left, which stays mounted beneath it, and "Back" returns there.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import AgedCareCostsExplained from './AgedCareCostsExplained.tsx';
import AgedCareHub from './AgedCareHub.tsx';
import { getAgedCareScreenContext, setAgedCareScreenContext } from './agedCareScreenContext.ts';
import { openAgedCareDigitalSOAAttachment } from './AgedCareSuggestionEngineDigitalSOAAttachment.tsx';
import type { AgedCareAnalyserProps, SavedAgedCareStrategy, SavedCaseStudy } from './AgedCareAnalyser.tsx';

export interface AgedCarePageProps extends Omit<AgedCareAnalyserProps, 'onBack'> {
    /** AgedCareAnalyser is injected from App.js rather than imported directly here. */
    AgedCareAnalyser: React.ComponentType<AgedCareAnalyserProps>;
}

export const REOPEN_INTENT_KEY = 'careCompanionReopenIntent';

const AgedCarePage = ({
    setActivePage,
    factFind,
    setFactFind,
    strategyOptions,
    setStrategyOptions,
    legacyYears,
    setLegacyYears,
    selectedStrategy,
    setSelectedStrategy,
    coupleValidation,
    setCoupleValidation,
    savedStrategies,
    setSavedStrategies,
    savedCaseStudies,
    setSavedCaseStudies,
    isProfessionalJourney = false,
    professionalCaseId = null,
    onSoaGenerated = null,
    onStrategySaved = null,
    AgedCareAnalyser,
}: AgedCarePageProps) => {
    const plans = useMemo(() => (savedStrategies || []).filter((s) => s?.type === 'aged_care'), [savedStrategies]);
    // The chosen plan: the in-memory selection when there is one, else the plan the file marks as chosen
    // (the selection does not survive a reload; the mark does).
    const chosen = useMemo(() => {
        const sel = selectedStrategy as { id?: unknown; name?: string } | null;
        const bySelection = sel ? plans.find((p) => (sel.id != null && String(p.id) === String(sel.id)) || (p.strategy && sel.name && p.strategy.name === sel.name && !sel.id)) ?? null : null;
        return bySelection ?? plans.find((p) => (p as { chosen?: boolean }).chosen) ?? null;
    }, [plans, selectedStrategy]);
    const documents = useMemo(
        () => (savedCaseStudies || []).filter((d) => !professionalCaseId || !d.caseId || d.caseId === professionalCaseId),
        [savedCaseStudies, professionalCaseId],
    );

    // A reopen token means "land on the plan"; otherwise plans on the file mean the landing.
    const [view, setView] = useState<'hub' | 'analyser'>(() => {
        try {
            if (sessionStorage.getItem('careCompanionReopenStrategy') || sessionStorage.getItem('careCompanionModelAnotherHome') || sessionStorage.getItem('careCompanionStartFresh')) return 'analyser';
        } catch { /* no storage: fall through */ }
        return plans.length > 0 ? 'hub' : 'analyser';
    });
    const [guideOpen, setGuideOpen] = useState(false);

    useEffect(() => {
        scrollAppToTop();
    }, [view, guideOpen]);
    // The guide, when open, is what AWise should talk about; the page beneath restores its own context on close.
    useEffect(() => {
        if (!guideOpen) return;
        const before = getAgedCareScreenContext();
        setAgedCareScreenContext({ screen: 'guide', note: '' });
        return () => setAgedCareScreenContext(before);
    }, [guideOpen]);

    const openPlan = (plan: SavedAgedCareStrategy, intent?: 'explain' | 'risks' | 'compare' | 'document' | 'detail' | 'room' | 'build' | 'build-new') => {
        try {
            sessionStorage.setItem('careCompanionReopenStrategy', JSON.stringify(plan));
            if (intent) sessionStorage.setItem(REOPEN_INTENT_KEY, intent); else sessionStorage.removeItem(REOPEN_INTENT_KEY);
        } catch { /* no storage: the analyser opens fresh */ }
        setView('analyser');
    };
    const modelAnotherHome = (from: SavedAgedCareStrategy | null) => {
        try {
            sessionStorage.removeItem('careCompanionReopenStrategy');
            sessionStorage.setItem('careCompanionModelAnotherHome', JSON.stringify(from ?? {}));
        } catch { /* no storage: the analyser opens fresh */ }
        setView('analyser');
    };
    /** A new model from scratch: nothing carried over, and the setup questions asked again (AgedCareAnalyser). */
    const startFresh = () => {
        try {
            sessionStorage.removeItem('careCompanionReopenStrategy');
            sessionStorage.removeItem('careCompanionModelAnotherHome');
            sessionStorage.removeItem(REOPEN_INTENT_KEY);
            sessionStorage.setItem('careCompanionStartFresh', '1');
        } catch { /* no storage: the analyser opens fresh, without re-asking the questions */ }
        setView('analyser');
    };
    const openDocument = (doc: SavedCaseStudy) => {
        const snapshot = (doc as { soaSnapshot?: unknown }).soaSnapshot;
        if (snapshot) {
            openAgedCareDigitalSOAAttachment(snapshot as Parameters<typeof openAgedCareDigitalSOAAttachment>[0]);
            return;
        }
        if (chosen) openPlan(chosen, 'document');
    };

    return (
        <>
            {guideOpen && (
                <AgedCareCostsExplained
                    setActivePage={setActivePage}
                    onContinue={() => setGuideOpen(false)}
                    onBack={() => setGuideOpen(false)}
                    backLabel="Back to where you were"
                    continueLabel="Back to where you were"
                    factFind={factFind}
                />
            )}
            {/* The page beneath the guide stays mounted (hidden), so nothing typed is lost. */}
            <div className={guideOpen ? 'hidden' : undefined} aria-hidden={guideOpen || undefined}>
                {view === 'hub' ? (
                    <AgedCareHub
                        factFind={factFind}
                        plans={plans}
                        chosen={chosen}
                        documents={documents}
                        onOpenPlan={openPlan}
                        onModelAnotherHome={modelAnotherHome}
                        onStartFresh={startFresh}
                        onOpenDocument={openDocument}
                        onReadGuide={() => setGuideOpen(true)}
                        onAssumptions={() => setActivePage('AssumptionsPage')}
                        goHome={() => setActivePage('Dashboard')}
                    />
                ) : (
                    <AgedCareAnalyser
                        setActivePage={setActivePage}
                        factFind={factFind}
                        setFactFind={setFactFind}
                        strategyOptions={strategyOptions}
                        setStrategyOptions={setStrategyOptions}
                        onBack={() => setGuideOpen(true)}
                        onBackToPlan={plans.length > 0 ? () => setView('hub') : undefined}
                        legacyYears={legacyYears}
                        setLegacyYears={setLegacyYears}
                        selectedStrategy={selectedStrategy}
                        setSelectedStrategy={setSelectedStrategy}
                        coupleValidation={coupleValidation}
                        setCoupleValidation={setCoupleValidation}
                        savedStrategies={savedStrategies}
                        setSavedStrategies={setSavedStrategies}
                        savedCaseStudies={savedCaseStudies}
                        setSavedCaseStudies={setSavedCaseStudies}
                        isProfessionalJourney={isProfessionalJourney}
                        professionalCaseId={professionalCaseId}
                        onSoaGenerated={onSoaGenerated}
                        onStrategySaved={onStrategySaved}
                    />
                )}
            </div>
        </>
    );
};

export default AgedCarePage;
